/**
 * Shuffling a sheet so the same seed shuffles it the same way.
 *
 * A teacher printing a second version for another class wants a new order,
 * and a teacher reprinting last week's sheet wants the old one. Both come from
 * the seed in the address. `Math.random` gives neither, so the order comes
 * from a small generator of our own, started from the seed.
 */

/** FNV-1a over the seed's characters, as the generator's starting point. */
function seedNumber(seed: string): number {
  let hash = 0x811c9dc5;

  for (const character of seed) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 0x01000193);
  }

  return hash >>> 0;
}

/**
 * Mulberry32: small, fast and well enough spread to deal a few hundred cards.
 * Nothing here is a secret, so nothing here needs more.
 */
function generator(seed: string): () => number {
  let state = seedNumber(seed);

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let mixed = state;
    mixed = Math.imul(mixed ^ (mixed >>> 15), mixed | 1);
    mixed ^= mixed + Math.imul(mixed ^ (mixed >>> 7), mixed | 61);

    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/**
 * The items in an order drawn from the seed, the same order every time.
 * @param items - What to shuffle, left as it was.
 * @param seed - Any string. The same one always gives the same order.
 * @returns A shuffled copy.
 */
export function seededShuffle<Item>(
  items: readonly Item[],
  seed: string,
): readonly Item[] {
  const next = generator(seed);
  const shuffled = [...items];

  for (let at = shuffled.length - 1; at > 0; at -= 1) {
    const other = Math.floor(next() * (at + 1));
    [shuffled[at], shuffled[other]] = [shuffled[other]!, shuffled[at]!];
  }

  return shuffled;
}

/**
 * A coin tossed from a key, landing the same way every time for that key.
 *
 * For a choice made item by item rather than an order, such as which half of
 * a syllable a pinyin sheet leaves out. Keying it by the seed and the item
 * keeps each item's choice when the order around it changes.
 * @param key - Any string. The same one always lands the same way.
 */
export function seededToss(key: string): boolean {
  return generator(key)() < 0.5;
}

/**
 * A new seed, for a sheet that asks to be shuffled again.
 *
 * Six base-36 digits, which is two billion orders and still short enough to
 * read off a printed footer.
 * @param random - Where the randomness comes from, `Math.random` on a page.
 */
export function freshSeed(random: () => number): string {
  return Math.floor(random() * 36 ** 6)
    .toString(36)
    .padStart(6, "0");
}

/**
 * A sheet's items in the order its settings ask for: as given, or shuffled
 * from the seed.
 *
 * Each kind of sheet shuffles what it prints, after choosing it, so a
 * shuffle reorders the same items and never swaps one for another.
 */
export function sheetOrder<Item>(
  items: readonly Item[],
  state: { readonly order: "given" | "shuffled"; readonly seed: string },
): readonly Item[] {
  return state.order === "shuffled" ? seededShuffle(items, state.seed) : items;
}
